/** Field-type + editability vocabulary shared by the CRM Lists grid and the record panel (D2b).
 *
 * The grid's colDef builder wires AG-Grid cell editors from these same rules; the record panel
 * re-hosts equivalent editors vertically. Keeping the rules in ONE place means the two surfaces can
 * never disagree about which fields are editable or which editor a type gets. This module derives
 * only field TYPE / editor shape — never field STATUS (that comes authoritatively from
 * `record_field_states.v1`; spec §5.4-1).
 */
import type { CrmGridColumn } from '../../types';
import { isComputedColumn } from './crmSystemFields';

export const REF_TYPES = new Set(['person', 'person_multi', 'organization', 'organization_multi']);
export const NUMERIC_TYPES = new Set(['number', 'currency']);
// inline-editable scalar types. ranked_enum (the IOT stage) is editable ONLY when the options endpoint
// supplies its vocabulary (M3.3) — it then gets a select editor validated server-side.
export const EDITABLE_TYPES = new Set(['text', 'long_text', 'number', 'currency', 'date', 'enum']);
// enum-family types carry an option picklist (config_json.options).
export const OPTION_TYPES = new Set(['enum', 'multi_enum', 'ranked_enum']);
/** PR3 item 7: the `CrmGridColumn.ref_options_source` value meaning "this ref column's eligible people
 *  are the active Ember roster" (backend `lists.ROSTER_REF_OPTION_SOURCE`). ⚠ The ONE place the FE
 *  decides a column gets the roster picker — never a field-key check, so a new roster-only field on the
 *  server needs no FE release. */
export const ROSTER_REF_SOURCE = 'roster';

export type PanelEditorKind =
  | 'text' | 'textarea' | 'number' | 'date'
  | 'select' | 'checklistSingle' | 'checklistMulti' | 'ref' | 'rosterRef';

/**
 * The record panel's per-field editability decision, mirroring the grid's buildColumnDefs rules:
 *  - `edit_mode` gates everything: 'direct' (native, all types incl. refs/multi_enum) vs 'overlay'
 *    (Affinity-backed — scalars only; refs/multi_enum are refused by the backend, a future push).
 *  - a COMPUTED column is never editable — `isComputedColumn` (read_only / 'derived' / 'view'), not
 *    an `origin` string. ⚠ W5 flipped a system column's origin to 'system', which is ALSO what the
 *    stored, editable `name` column on every opportunity list carries; branching on the string would
 *    have made every system cell editable and the IOT's deal name read-only, in one change.
 *  - enum/ranked_enum need an option picklist to get a picker; a bare `enum` with no options falls
 *    back to free text (matching the grid's agTextCellEditor); a `ranked_enum` with no options is
 *    read-only (matching the grid, which only makes it editable once options load).
 *  - a long (>20) picklist gets the searchable checklist instead of a native <select>.
 *  - PR3 item 7: a ref column flagged `ref_options_source:'roster'` gets the roster CHECKLIST
 *    (`rosterRef`) instead of the open directory typeahead — but only once its picklist has arrived;
 *    with no people yet (loading, or the backend omitted it) it falls back to `ref`, which is a working
 *    editor whose non-roster picks the server still refuses. An empty picker would be a dead end.
 * Returns `{ editable:false, kind:null }` for anything the operator may not edit here.
 */
export function panelFieldEditor(
  col: CrmGridColumn,
  editMode: 'direct' | 'overlay' | undefined,
  opts: string[] | undefined,
  /** the column's roster picklist (`CrmListOptionsResponse.ref_options[field_key]`), when it has one */
  refPeople?: { local_person_id: number; name: string }[] | undefined,
): { editable: boolean; kind: PanelEditorKind | null } {
  const NO = { editable: false, kind: null } as const;
  if (editMode !== 'direct' && editMode !== 'overlay') return NO;
  if (isComputedColumn(col)) return NO;
  const isNative = editMode === 'direct';
  const ft = col.field_type;
  const hasOpts = !!(opts && opts.length);

  if (REF_TYPES.has(ft)) {
    if (!isNative) return NO;
    const roster = col.ref_options_source === ROSTER_REF_SOURCE && !!refPeople?.length;
    return { editable: true, kind: roster ? 'rosterRef' : 'ref' };
  }
  // C3 (§6.3): multi_enum is editable on BOTH native and overlay lists — the backend accepts overlay
  // multi_enum writes (they route through _set_overlay_value). Only entity refs stay native-only.
  if (ft === 'multi_enum') return hasOpts ? { editable: true, kind: 'checklistMulti' } : NO;
  if (ft === 'enum' || ft === 'ranked_enum') {
    if (hasOpts) return { editable: true, kind: opts!.length > 20 ? 'checklistSingle' : 'select' };
    return ft === 'enum' ? { editable: true, kind: 'text' } : NO; // ranked_enum w/o vocab → read-only
  }
  if (ft === 'long_text') return { editable: true, kind: 'textarea' };
  if (NUMERIC_TYPES.has(ft)) return { editable: true, kind: 'number' };
  if (ft === 'date') return { editable: true, kind: 'date' };
  if (ft === 'text') return { editable: true, kind: 'text' };
  return NO;
}
